import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ops, asList, fmtDate, OPS } from "@/admin/api";
import { PageTitle, EmptyState, Pill, ActionButton, Table, cell } from "@/admin/ui";

const TABS = [
  { key: "convoys", label: "Active Convoys" },
  { key: "sos", label: "SOS Alert Log" },
];

export default function Convoys() {
  const [tab, setTab] = useState("convoys");
  const [convoys, setConvoys] = useState(null);
  const [sos, setSos] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    ops(OPS.convoys).then((d) => setConvoys(asList(d, "convoys"))).catch((e) => setError(e.message));
    ops(OPS.sos).then((d) => setSos(asList(d, "alerts", "sos"))).catch(() => setSos([]));
  }, []);

  const disband = async (id) => {
    try {
      await ops(OPS.disbandConvoy(id), { method: "POST" });
      setConvoys((c) => c.filter((x) => (x.id || x.convoyId) !== id));
      toast.success("Convoy disbanded");
    } catch (e) {
      toast.error(`Disband failed: ${e.message}`);
    }
  };

  return (
    <div data-testid="admin-convoys-page">
      <PageTitle eyebrow="Admin Portal" title="Convoy Oversight" />
      <div className="mb-6 flex gap-2" data-testid="convoys-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-lg border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
              tab === t.key ? "border-[#FF3333]/40 bg-[#FF3333]/10 text-white" : "border-white/10 text-neutral-400 hover:text-white"
            }`}
            data-testid={`convoys-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "convoys" && (
        error ? (
          <EmptyState title="Convoy list unavailable" hint={error} />
        ) : !convoys ? (
          <p className="label-tech text-neutral-500">Loading…</p>
        ) : !convoys.length ? (
          <EmptyState title="No active convoys" hint="Live convoys with participants, leader, start time and destination will appear here." />
        ) : (
          <Table head={["Convoy", "Leader", "Riders", "Started", "Destination", ""]} testId="convoys-table">
            {convoys.map((c, i) => {
              const id = c.id || c.convoyId || i;
              return (
                <tr key={id} data-testid={`convoy-row-${id}`}>
                  <td className={cell}>
                    <p className="font-semibold text-white">{c.name || c.code || id}</p>
                    <p className="text-xs text-neutral-500">{c.code ? `#${c.code}` : ""}</p>
                  </td>
                  <td className={cell}>{c.leaderName || c.leader || (c.leaderId ? `${String(c.leaderId).slice(0, 8)}…` : "—")}</td>
                  <td className={cell}>
                    <Pill tone="cyan">{c.memberCount ?? c.participantCount ?? c.members?.length ?? "—"} live</Pill>
                  </td>
                  <td className={cell}>{fmtDate(c.tripStartedAt || c.startedAt || c.createdAt)}</td>
                  <td className={cell}>{c.destination?.address || c.destination || "—"}</td>
                  <td className={`${cell} text-right`}>
                    <ActionButton tone="red" onClick={() => disband(id)} data-testid={`convoy-disband-${id}`}>Force disband</ActionButton>
                  </td>
                </tr>
              );
            })}
          </Table>
        )
      )}

      {tab === "sos" && (
        !sos ? (
          <p className="label-tech text-neutral-500">Loading…</p>
        ) : !sos.length ? (
          <EmptyState title="No SOS alerts on record" hint="Alerts appear here with timestamp, affected user, location and responding riders." />
        ) : (
          <Table head={["Time", "User", "Location", "Responding riders", "Status"]} testId="sos-table">
            {sos.map((a, i) => (
              <tr key={a.id || i} data-testid={`sos-row-${a.id || i}`}>
                <td className={cell}>{fmtDate(a.createdAt || a.timestamp)}</td>
                <td className={`${cell} font-semibold text-white`}>{a.userName || a.userId || "—"}</td>
                <td className={cell}>
                  {a.location ? `${a.location.lat ?? a.location.latitude}, ${a.location.lng ?? a.location.longitude}` : "—"}
                </td>
                <td className={cell}>{a.responders?.length ?? a.respondingRiders ?? "—"}</td>
                <td className={cell}>
                  <Pill tone={a.resolved ? "green" : "red"}>{a.resolved ? "resolved" : "active"}</Pill>
                </td>
              </tr>
            ))}
          </Table>
        )
      )}
    </div>
  );
}
